import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CFormInput,
  CFormSelect,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import type { ApiError } from "../../api/client";
import { useFollowedStrategies, useSaveFollowedStrategies } from "../../api/hooks/useFollowedStrategies";
import type { TradingStrategyDto } from "../../api/hooks/useTradingStrategies";
import { useFailure } from "../../context/useFailure";
import { useToast } from "../../context/useToast";
import { routePaths } from "../../routes/route-paths";
import { FollowTable } from "./components/FollowTable";
import { StrategyMarksKey } from "./components/StrategyMarksKey";
import { SUBSCRIBED_SOURCE, useFollowableStrategies } from "./useFollowableStrategies";

// Mirrors CStepper's exposed imperative API, as ProfileEditWizard does.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

const ALL_SOURCES = "all";

type Show = "all" | "ticked" | "unticked";

const SHOW_FILTERS: { value: Show; label: string }[] = [
  { value: "all", label: "configure.wizard.showAll" },
  { value: "ticked", label: "configure.wizard.showTicked" },
  { value: "unticked", label: "configure.wizard.showUnticked" },
];

function matchesSearch(strategy: TradingStrategyDto, search: string): boolean {
  const term = search.trim().toLowerCase();
  if (term === "") {
    return true;
  }

  const searchable = [
    strategy.publicName,
    strategy.chartedSymbol,
    strategy.bullSymbol,
    strategy.bearSymbol,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ")
    .toLowerCase();

  return searchable.includes(term);
}

/**
 * Tick the strategies to follow, review what changes, then save once. Nothing is saved before the
 * review, so a whole list of changes is a single request.
 */
export function ConfigureSignalsWizard() {
  const { t } = useTranslation("signals");
  const navigate = useNavigate();
  const { showSuccess, showWarning } = useToast();
  const { showFailure, clearFailure } = useFailure();
  const followable = useFollowableStrategies();
  const followed = useFollowedStrategies();
  const save = useSaveFollowedStrategies();

  const stepperRef = useRef<CStepperRef>(null);
  const [, setCurrentStep] = useState(1);
  // Only what the customer changed, against what they follow now: true to follow, false to stop.
  const [changes, setChanges] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [search, setSearch] = useState("");
  const [source, setSource] = useState(ALL_SOURCES);
  const [show, setShow] = useState<Show>("all");
  const [showEverything, setShowEverything] = useState(false);

  const followedIds = new Set(followed.data ?? []);
  const isTicked = (id: string) => changes.get(id) ?? followedIds.has(id);
  const ticked = new Set(followable.strategies.filter((s) => isTicked(s.id)).map((s) => s.id));

  const setTicked = (ids: string[], value: boolean) =>
    setChanges((previous) => {
      const next = new Map(previous);
      for (const id of ids) {
        if (value === followedIds.has(id)) {
          next.delete(id);
        } else {
          next.set(id, value);
        }
      }
      return next;
    });

  const sourceIds =
    source === ALL_SOURCES
      ? null
      : (followable.sources.find((candidate) => candidate.key === source)?.strategyIds ?? null);
  const visible = followable.strategies.filter((strategy) => {
    const sourceMatches = sourceIds === null || sourceIds.has(strategy.id);
    const showMatches = show === "all" || (show === "ticked") === ticked.has(strategy.id);
    return sourceMatches && showMatches && matchesSearch(strategy, search);
  });

  const toFollow = followable.strategies.filter((strategy) => changes.get(strategy.id) === true);
  const toStop = followable.strategies.filter((strategy) => changes.get(strategy.id) === false);
  const after = followable.strategies.filter((strategy) => ticked.has(strategy.id));

  const sourceLabel = (key: string) => {
    const found = followable.sources.find((candidate) => candidate.key === key);
    if (!found?.membership) {
      return t("configure.wizard.subscribed");
    }
    return t("configure.wizard.membership", {
      tier: found.membership.tierName,
      platform: found.membership.platformName,
    });
  };

  const handleSave = async () => {
    clearFailure();
    try {
      await save.mutateAsync({
        follow: toFollow.map((strategy) => strategy.id),
        unfollow: toStop.map((strategy) => strategy.id),
      });
      showSuccess(t("configure.wizard.saved", { count: after.length }));
      navigate(routePaths.signalsConfigure);
    } catch (err) {
      const apiError = err as ApiError;
      const message = err instanceof Error ? err.message : t("configure.wizard.saveError");

      if (apiError?.status !== undefined && apiError.status < 500) {
        showWarning(message);
      } else {
        showFailure(message, apiError?.traceId);
      }
    }
  };

  const changeSummary =
    changes.size === 0
      ? t("configure.wizard.noChanges")
      : t("configure.wizard.changes", { follow: toFollow.length, unfollow: toStop.length });

  const isLoading = followable.isLoading || followed.isLoading;
  const loadError = followable.error ?? followed.error;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (loadError) {
    return <CAlert color="danger">{t("configure.loadError")}</CAlert>;
  }

  if (followable.strategies.length === 0) {
    return (
      <CAlert color="info">
        {t("empty.noStrategyBody")} <Link to={routePaths.strategies}>{t("empty.browseStrategies")}</Link>
      </CAlert>
    );
  }

  const changeHeading = (count: number, label: string, tone: "success" | "danger") => (
    <h3 className="h6 d-flex align-items-center gap-2 mb-0">
      <span className={`badge rounded-pill tinted-${tone} text-body`}>{count}</span>
      {label}
    </h3>
  );

  const steps = [
    {
      label: t("configure.wizard.choose"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("configure.wizard.choose")}</h4>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-3">
            <div className="d-flex flex-wrap gap-2">
              <CFormInput
                type="search"
                placeholder={t("configure.wizard.search")}
                aria-label={t("configure.wizard.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: "1 1 14rem", minWidth: 0 }}
              />
              {followable.sources.length > 1 && (
                <CFormSelect
                  aria-label={t("configure.wizard.source")}
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  style={{ flex: "1 1 14rem", maxWidth: "20rem" }}
                >
                  <option value={ALL_SOURCES}>{t("configure.wizard.allSources")}</option>
                  {followable.sources.map((candidate) => (
                    <option key={candidate.key} value={candidate.key}>
                      {candidate.key === SUBSCRIBED_SOURCE
                        ? t("configure.wizard.subscribed")
                        : sourceLabel(candidate.key)}
                    </option>
                  ))}
                </CFormSelect>
              )}
              <CButtonGroup role="group" aria-label={t("configure.wizard.showFilter")}>
                {SHOW_FILTERS.map((filter) => (
                  <CButton
                    key={filter.value}
                    color="primary"
                    variant={show === filter.value ? undefined : "outline"}
                    aria-pressed={show === filter.value}
                    onClick={() => setShow(filter.value)}
                  >
                    {t(filter.label)}
                  </CButton>
                ))}
              </CButtonGroup>
            </div>

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 small text-body-secondary">
              <span>{t("configure.wizard.count", { count: visible.length })}</span>
              <span className="d-flex gap-3">
                <CButton
                  color="link"
                  size="sm"
                  className="p-0"
                  disabled={visible.length === 0 || visible.every((s) => ticked.has(s.id))}
                  onClick={() => setTicked(visible.map((s) => s.id), true)}
                >
                  {t("configure.wizard.tickAll", { count: visible.length })}
                </CButton>
                <CButton
                  color="link"
                  size="sm"
                  className="p-0"
                  disabled={visible.every((s) => !ticked.has(s.id))}
                  onClick={() => setTicked(visible.map((s) => s.id), false)}
                >
                  {t("configure.wizard.untickAll")}
                </CButton>
              </span>
            </div>

            {visible.length > 0 ? (
              <FollowTable
                key={`${search}|${source}|${show}`}
                strategies={visible}
                ticked={ticked}
                onToggle={(id) => setTicked([id], !isTicked(id))}
              />
            ) : (
              <p className="text-body-secondary mb-0">{t("configure.wizard.noMatches")}</p>
            )}
            <StrategyMarksKey strategies={visible} />

            <hr className="my-0" />
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span className="small text-body-secondary">{changeSummary}</span>
              <span className="d-flex gap-2">
                <CButton
                  color="secondary"
                  variant="outline"
                  onClick={() => navigate(routePaths.signalsConfigure)}
                >
                  {t("configure.wizard.cancel")}
                </CButton>
                <CButton
                  color="primary"
                  disabled={changes.size === 0}
                  onClick={() => {
                    setShowEverything(false);
                    stepperRef.current?.next();
                  }}
                >
                  {t("configure.wizard.toReview")}
                </CButton>
              </span>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: t("configure.wizard.review"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("configure.wizard.review")}</h4>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-3">
            <p className="text-body-secondary mb-0">{t("configure.wizard.after", { count: after.length })}</p>

            {toFollow.length > 0 && (
              <>
                {changeHeading(toFollow.length, t("configure.wizard.startFollowing"), "success")}
                <FollowTable strategies={toFollow} />
              </>
            )}

            {toStop.length > 0 && (
              <>
                {changeHeading(toStop.length, t("configure.wizard.stopFollowing"), "danger")}
                <FollowTable strategies={toStop} />
              </>
            )}

            <div>
              <CButton color="link" size="sm" className="p-0" onClick={() => setShowEverything(!showEverything)}>
                {showEverything
                  ? t("configure.wizard.hideAll")
                  : t("configure.wizard.seeAll", { count: after.length })}
              </CButton>
            </div>
            {showEverything && <FollowTable strategies={after} />}

            <StrategyMarksKey strategies={showEverything ? after : [...toFollow, ...toStop]} />

            <hr className="my-0" />
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <CButton
                color="secondary"
                variant="outline"
                onClick={() => stepperRef.current?.prev()}
                disabled={save.isPending}
              >
                {t("configure.wizard.back")}
              </CButton>
              <CButton color="primary" onClick={handleSave} disabled={save.isPending}>
                {save.isPending && <CSpinner size="sm" className="me-2" />}
                {t("configure.wizard.save")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>{t("configure.wizard.title")}</h2>
      <p className="text-body-secondary">{t("configure.wizard.description")}</p>
      <hr />

      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}

export default ConfigureSignalsWizard;
